import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Upload, X, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { description: "", dimensions: "", color: "", quantity: "1", intended_use: "", needed_by: "", name: "", email: "", phone: "", city: "" };

export default function Disena() {
  const { t } = useLang();
  const [form, setForm] = useState(empty);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.name || !form.email || !form.phone) {
      toast.error(t.disena.required);
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/design-requests`, fd);
      setDone(true);
    } catch {
      toast.error("Error al enviar. Intenta de nuevo.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="design-success" className="pt-16 min-h-screen flex items-center justify-center px-5">
        <div className="text-center max-w-md">
          <CheckCircle2 size={56} className="text-[#2E5A3B] mx-auto mb-6" />
          <h1 className="font-serif-d text-4xl font-semibold text-[#132A1D] mb-4">{t.disena.successTitle}</h1>
          <p className="text-[#5C5C5C] leading-relaxed mb-8">{t.disena.successMsg}</p>
          <Button data-testid="design-another-btn" onClick={() => { setDone(false); setForm(empty); setFiles([]); }}
            className="rounded-full bg-[#2E5A3B] hover:bg-[#C86A4B] text-white px-8 h-11 transition-colors duration-300">
            {t.disena.another}
          </Button>
        </div>
      </div>
    );
  }

  const field = (id, label, ph = "", type = "text", required = false) => (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-semibold text-[#132A1D]">{label}{required && <span className="text-[#C86A4B]"> *</span>}</Label>
      <Input id={id} data-testid={`design-${id}`} type={type} placeholder={ph} value={form[id]} onChange={set(id)}
        className="bg-white border-black/10 focus-visible:ring-[#2E5A3B] h-11 rounded-xl" />
    </div>
  );

  return (
    <div data-testid="design-page" className="pt-16 min-h-screen">
      <Seo page="disena" />
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <Reveal>
          <h1 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#132A1D] tracking-tight">{t.disena.title}</h1>
          <p className="text-[#5C5C5C] mt-4 leading-relaxed">{t.disena.sub}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <form onSubmit={submit} className="mt-12 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-semibold text-[#132A1D]">{t.disena.desc}<span className="text-[#C86A4B]"> *</span></Label>
              <Textarea id="description" data-testid="design-description" rows={4} placeholder={t.disena.descPh}
                value={form.description} onChange={set("description")}
                className="bg-white border-black/10 focus-visible:ring-[#2E5A3B] rounded-xl" />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#132A1D]">{t.disena.files}</Label>
              <button type="button" data-testid="design-file-upload" onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-[#2E5A3B]/30 rounded-2xl p-8 text-center hover:border-[#2E5A3B] hover:bg-[#2E5A3B]/5 transition-colors duration-200">
                <Upload size={24} className="mx-auto text-[#2E5A3B] mb-2" />
                <p className="text-sm text-[#5C5C5C]">{t.disena.filesHint}</p>
              </button>
              <input ref={fileRef} type="file" multiple accept="image/*,.stl,.pdf,.obj,.3mf" className="hidden"
                onChange={(e) => setFiles([...files, ...Array.from(e.target.files)].slice(0, 5))} />
              {files.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {files.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 bg-[#EFECE6] rounded-full px-3 py-1.5 text-xs font-medium">
                      {f.name}
                      <button type="button" data-testid={`design-file-remove-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))}><X size={12} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              {field("dimensions", t.disena.dims, t.disena.dimsPh)}
              {field("color", t.disena.color, t.disena.colorPh)}
              {field("quantity", t.disena.qty, "", "number")}
              {field("needed_by", t.disena.date, "", "date")}
            </div>
            {field("intended_use", t.disena.use, t.disena.usePh)}

            <div className="border-t border-black/10 pt-6 grid sm:grid-cols-2 gap-6">
              {field("name", t.disena.name, "", "text", true)}
              {field("email", t.disena.email, "", "email", true)}
              {field("phone", t.disena.phone, "", "tel", true)}
              {field("city", t.disena.city)}
            </div>

            <Button type="submit" data-testid="design-submit-btn" disabled={sending}
              className="w-full rounded-full bg-[#C86A4B] hover:bg-[#2E5A3B] text-white h-13 py-6 text-base font-semibold transition-colors duration-300">
              {sending ? <><Loader2 className="animate-spin mr-2" size={18} />{t.disena.sending}</> : t.disena.submit}
            </Button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
